import { Button, Form, Input, Space } from "antd";
import { useEffect } from "react";
import {
  useAddExpenseUnitMutation,
  useGetExpenseUnitDetailsQuery,
  useUpdateExpenseUnitMutation,
} from "../../../features/expenseUnit/expenseUnitApi";
import useError from "../../../hooks/useError";
import { toast } from "../../../lib/toast";
import { ModalComponentProps } from "../../../types";
import EmployeeFileUpload from "../../Employees/EmployeeFileUpload";
import CustomModal from "../../ui/CustomModal";
import Loader from "../../ui/Loader";

export default function ManageExpenseUnit({
  handleClose,
  type,
  data,
  ...rest
}: ModalComponentProps<string>) {
  const [form] = Form.useForm();
  const {
    isLoading: isExpenseUnitLoading,
    isError: isExpenseUnitDetailsError,
    error: expenseUnitDetailsError,
    data: expenseUnitDetails,
  } = useGetExpenseUnitDetailsQuery(data || "", {
    skip: !data && type !== "UPDATE",
  });
  const [addExpenseUnit, { isLoading, isError, isSuccess, error }] =
    useAddExpenseUnitMutation();
  const [
    updateExpenseUnit,
    {
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      isSuccess: isUpdateSuccess,
      error: updateError,
    },
  ] = useUpdateExpenseUnitMutation();
  // error toast message
  useError(isError, error, "Expense unit added fail");
  useError(isUpdateError, updateError, "Expense unit updated fail");
  useError(
    isExpenseUnitDetailsError,
    expenseUnitDetailsError,
    "Get expense unit fail"
  );

  const onFinish = (values: unknown) => {
    if (type === "UPDATE" && data) {
      return updateExpenseUnit({ body: values, id: data });
    }

    return addExpenseUnit(values);
  };

  useEffect(() => {
    if (Object.keys(expenseUnitDetails?.data || {}) && type === "UPDATE") {
      const expenseUnit = expenseUnitDetails?.data;

      form.setFieldsValue({
        name: expenseUnit?.name,
        abbreviation: expenseUnit?.abbreviation,
        logo: expenseUnit?.logo,
      });
    }
  }, [form, expenseUnitDetails?.data, type]);

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      toast("success", "Expense unit added successfully");
      return handleClose();
    }
    if (isUpdateSuccess) {
      toast("success", "Expense unit updated successfully");
      return handleClose();
    }
  }, [handleClose, isSuccess, isUpdateSuccess]);

  return (
    <CustomModal
      title={`${type === "UPDATE" ? "Update" : "Add New"} Expense unit`}
      {...rest}
      handleClose={handleClose}
      width={460}
      footer={null}
    >
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        {isExpenseUnitLoading ? (
          <Loader />
        ) : (
          <>
            <Form.Item
              name="logo"
              label="Logo"
              className="mb-4"
              valuePropName="fileUrl"
            >
              <EmployeeFileUpload
                form={form}
                accept=".png, .jpg, .jpeg, .gif"
                name="logo"
                onChange={(url) =>
                  form.setFieldsValue({
                    logo: url,
                  })
                }
              />
            </Form.Item>
            <Form.Item
              name="name"
              label="Expense unit name"
              className="mb-4"
              rules={[
                {
                  required: true,
                  message: "Please enter name",
                },
              ]}
            >
              <Input size="large" placeholder="Enter name" />
            </Form.Item>
            <Form.Item
              name="abbreviation"
              label="Abbreviation"
              className="mb-4"
            >
              <Input size="large" placeholder="Ex: HRM" />
            </Form.Item>
          </>
        )}

        <Space className="w-full mt-2" size={12}>
          <Button
            className="normal-dark-button modal-button"
            onClick={handleClose}
            size="middle"
            disabled={isLoading || isUpdateLoading}
          >
            Cancel
          </Button>
          <Button
            className="modal-button"
            type="primary"
            htmlType="submit"
            size="middle"
            loading={isLoading || isUpdateLoading}
            disabled={isExpenseUnitLoading}
          >
            {type === "UPDATE" ? "Update" : "Save"}
          </Button>
        </Space>
      </Form>
    </CustomModal>
  );
}
